import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { FindPwTemplate } from "components/entrance/signin";
import Alert from "components/ui/Alert";
import { fetchGetAuthNumber } from "redux/slices/signin/signinSlice";
import { getPhoneNumberFormat } from "utils/format";
import { fetchResetPassword } from "redux/slices/signin/signinSlice";
import Toast from "components/ui/Toast";
import CryptoJS from "crypto-js";

function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest function.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

const FindPassword = () => {
  const dispatch = useDispatch();
  const [authCount, setAuthCount] = useState(0);
  const [signinInfo, setSigninInfo] = useState({
    phoneNumber: "010-",
    name: "",
    authNumber: "",
    authNumberFromUser: "",
    passCode: "",
    passCodeConfirm: "",
  });
  const navigate = useNavigate();

  useInterval(
    () => {
      setAuthCount(authCount - 1);
    },
    authCount === 0 ? null : 1000
  );

  const handleChange = (e, name) => {
    if (name === "phoneNumber") {
      const numFormat = getPhoneNumberFormat(e.target.value);
      if (numFormat) {
        setSigninInfo((previousValue) => ({
          ...previousValue,
          phoneNumber: numFormat,
        }));
      }
      return;
    }
    setSigninInfo((previousValue) => ({
      ...previousValue,
      [name]: e.target.value,
    }));
  };

  const handleClickAuth = (authSecond, setOpen) => {
    if (authSecond > 150) {
      Alert(`30초 안에는 다시 보낼 수 없습니다<br/>
      잠시 후 다시 요청해주세요`);
      return;
    }
    let updatePhoneNumber = signinInfo.phoneNumber;
    if (signinInfo.phoneNumber.replace(/-/gi, "").length === 10) {
      const numFull = signinInfo.phoneNumber.replace(/-/gi, "");
      updatePhoneNumber = `${numFull.slice(0, 3)}-${numFull.slice(
        3,
        6
      )}-${numFull.slice(6, 10)}`;
    }

    dispatch(
      fetchGetAuthNumber({ ...signinInfo, phoneNumber: updatePhoneNumber })
    )
      .unwrap()
      .then(() => {
        setOpen(true);
        setAuthCount(180);
      })
      .catch((error) => {
        console.log("fetchGetAuthNumber error", error);
      });
  };

  const handleClickDone = () => {
    if (authCount === 0) {
      Alert(`인증시간이 초과되었습니다<br/>
      인증번호를 다시 요청해주세요`);
    }

    if (!signinInfo.authNumberFromUser) {
      Alert("인증번호를 정확히 입력해주세요.");
      return;
    }

    if (signinInfo.passCode !== signinInfo.passCodeConfirm) {
      Alert("비밀번호가 올바르지않습니다.<br/>다시 시도해주세요.");
      return;
    }

    if (signinInfo.passCode?.length < 8) {
      Alert("비밀번호를 8자 이상 입력해주세요.");
      return;
    }

    if (signinInfo.passCodeConfirm?.length < 8) {
      Alert("비밀번호를 8자 이상 입력해주세요.");
      return;
    }

    const secretkey = "bodycodi-manager";
    const key = CryptoJS.enc.Utf8.parse(secretkey.substring(0, 32));
    const iv = CryptoJS.enc.Utf8.parse(secretkey.substring(0, 16));
    const passCode = CryptoJS.AES.encrypt(signinInfo.passCode, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }).toString();

    dispatch(
      fetchResetPassword({
        phoneNumber: signinInfo.phoneNumber,
        authenticationNumber: signinInfo.authNumberFromUser,
        passCode,
      })
    )
      .unwrap()
      .then((result) => {
        if (result) {
          Toast("비밀번호가 변경되었습니다", "key");
          navigate("/entrance/signin");
        }
      })
      .catch((e) => {});
  };

  return (
    <FindPwTemplate
      title={`비밀번호 찾기에\n필요한 정보를 입력해주세요`}
      inputValue={signinInfo}
      inputValueChange={handleChange}
      buttonOnClick={handleClickDone}
      onClickAuth={handleClickAuth}
      authCount={authCount}
      setAuthCount={setAuthCount}
    />
  );
};
FindPassword.displayName = "FindPassword";
export default FindPassword;
